import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useData } from "../../context/DataContext";
import { QualificationForm } from "../../components/qualifications/QualificationForm";
import api from "../../utils/api";
import { Qualification, User } from "../../types";
import { ChevronLeft, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { Card } from "../../components/ui/card";
import { ShieldAlert } from "lucide-react";

export default function EditQualificationPage() {
  const { personId, qualificationId } = useParams<{ personId: string; qualificationId: string }>();
  const navigate = useNavigate();
  const { updateQualification, fetchQualifications } = useData();
  const [qualification, setQualification] = useState<Qualification | null>(null);
  const [person, setPerson] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const ability = useAbility(AbilityContext);

  useEffect(() => {
    const fetchData = async () => {
      if (!personId || !qualificationId) return;
      setIsLoading(true);
      try {
        const [qRes, uRes] = await Promise.all([
          api.get(`/api/qualifications/${qualificationId}`, { skipErrorToast: true }),
          api.get(`/api/users/${personId}`, { skipErrorToast: true })
        ]);

        if (qRes.success) {
          setQualification(qRes.qualification);
        }
        if (uRes.success) {
          setPerson(uRes.user);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        toast.error("Failed to load qualification details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [personId, qualificationId]);

  const handleSubmit = async (payload: any, selectedFiles: File[]) => {
    if (!qualificationId) return;
    setIsSubmitting(true);
    try {
      await updateQualification(qualificationId, payload);

      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const uploadFormData = new FormData();
          uploadFormData.append('image', file);
          uploadFormData.append('context', 'qualifications');

          try {
            const uploadResult = await api.post('/api/upload/s3', uploadFormData, { skipErrorToast: true });

            if (uploadResult.url) {
              await api.post('/api/qualifications/attachments', {
                qualificationId: qualificationId,
                filename: file.name,
                url: uploadResult.url,
                key: uploadResult.key || uploadResult.filename,
                size: uploadResult.size,
                mimetype: uploadResult.mimetype
              }, { skipErrorToast: true });
            }
          } catch (uploadError) {
            console.error(`Error uploading ${file.name}:`, uploadError);
            toast.error(`Failed to upload ${file.name}`);
          }
        }
      }

      // Success handled by DataContext
      // toast.success("Qualification updated successfully");
      await fetchQualifications();
      // Navigate back to the person's page with the qualifications tab active
        navigate(`/people/${personId}?tab=qualifications`);
    } catch (error) {
      console.error("Submit error:", error);
      // Error handled by api utility
      // toast.error("An error occurred while updating the qualification");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!ability.can(ACTIONS.UPDATE, RESOURCES.QUALIFICATION)) {
    return (
      <div className="container mx-auto py-20 flex justify-center">
        <Card className="border-slate-200 shadow-lg p-12 text-center bg-white max-w-2xl">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="bg-red-50 p-4 rounded-full">
              <ShieldAlert className="h-12 w-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Access Denied</h2>
            <p className="text-slate-500 max-w-md mx-auto">
              You do not have the required permissions to edit qualifications. 
              Please contact your system administrator if you believe this is an error.
            </p>
            <Button variant="outline" onClick={() => navigate("/qualifications")}>Back to Register</Button>
          </div>
        </Card>
      </div>
    );
  }

  const handleCancel = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        <p className="mt-2 text-slate-500 font-medium">Loading qualification details...</p>
      </div>
    );
  }

  if (!qualification) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <p className="text-slate-500 font-medium">Qualification not found.</p>
        <Button variant="outline" onClick={handleCancel} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      {/* Header */}
      <div className="space-y-4">
        <button 
          onClick={handleCancel}
          className="flex items-center text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Qualifications
        </button>
        
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Edit Qualification</h1>
          <p className="text-slate-500">
            Updating qualification for <span className="font-semibold text-slate-900">{person?.name || "the selected person"}</span>
          </p>
        </div>
      </div>

      <div className="mt-8">
        <QualificationForm 
          initialData={qualification}
          personId={personId}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isSubmitting}
          submitLabel="Update Qualification"
          isPage={true}
        />
      </div>
    </div>
  );
}
